import { Elements } from "@/components/elements/Elements";
import { WeatherParameters } from "@/components/elements/Weather/types";
import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { User } from "@/lib/types";
import { TodoType } from "@/services/todolist";
import { DBSchema, IDBPDatabase } from "idb";

export interface Schema extends DBSchema {
  appState:
    | {
        key: `elements.visibility.${string}`;
        value: boolean | null;
      }
    | {
        key: "activeProfileId";
        value: string | null;
      }
    | {
        key: "auth.dismissed";
        value: boolean;
      }
    | {
        key: "auth.user";
        value: User | null;
      }
    | {
        key: "auth.accessToken";
        value: string | null;
      }
    | {
        key: "ai.provider";
        value: string | null;
      };
  elements: {
    key: string;
    value: {
      id: string;
      name: keyof typeof Elements;
      parentId: string | null;
      childrenIds: string[];
      profileId: string | null;
      config: string;
    };
    indexes: {
      id: string;
      profileId: string;
    };
  };
  stickyNotes: {
    key: string;
    value: {
      elementId: string;
      content: string;
    };
  };
  weatherConfig: {
    key: string;
    value: {
      elementId: string;
    } & WeatherParameters;
  };
  todos: {
    key: string;
    value: TodoType;
    indexes: {
      id: string;
    };
  };
}

export const migration = async (db: IDBPDatabase<Schema>) => {
  const appStateStore = db.createObjectStore("appState");

  // Migrate ACCESSTOKEN
  const { accessToken, user } = JSON.parse(
    localStorage.getItem(`${LOCALSTORAGE_KEY}_ACCESSTOKEN`) ||
      '{"accessToken": null, "user": null}',
  );
  if (accessToken) {
    await appStateStore.add(accessToken, "auth.accessToken");
  }

  if (accessToken && user) {
    await appStateStore.add(user, "auth.user");
  }

  // Migrate ACTIVE_PROFILE_ID
  const activeProfileId = localStorage.getItem(
    `${LOCALSTORAGE_KEY}_ACTIVE_PROFILE_ID`,
  );
  if (activeProfileId) {
    await appStateStore.add(activeProfileId, "activeProfileId");
  }

  // Migrate APP_STATE
  const appState = JSON.parse(
    localStorage.getItem(`${LOCALSTORAGE_KEY}_APP_STATE`) || "{}",
  );
  await appStateStore.add(appState?.auth?.dismissed || false, "auth.dismissed");

  const elementsStore = db.createObjectStore("elements", {
    keyPath: "id",
  });
  elementsStore.createIndex("id", "id");
  elementsStore.createIndex("profileId", "profileId");

  const elements = JSON.parse(
    localStorage.getItem(`${LOCALSTORAGE_KEY}_ELEMENTS`) || "null",
  );
  if (elements) {
    await Promise.all(
      elements.map((element: any) => {
        return elementsStore.add({
          ...element,
          profileId: element.profileId || null,
        });
      }),
    );
  }

  // Migrate services
  const stickyNotes: {
    elementId: string;
    content: string;
  }[] = [];
  const weathers: ({ elementId: string } & WeatherParameters)[] = [];
  const todos: TodoType[] = JSON.parse(localStorage.getItem("todos") || "[]");

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(`${LOCALSTORAGE_KEY}_NOTE_`)) {
      const id = key.replace(`${LOCALSTORAGE_KEY}_NOTE_`, "");
      if (!id) {
        continue;
      }

      stickyNotes.push({
        elementId: id,
        content: localStorage.getItem(key) || "",
      });
    } else if (key?.startsWith(`${LOCALSTORAGE_KEY}_WEATHER_PARAMS_`)) {
      const params = JSON.parse(localStorage.getItem(key) || "{}");
      const id = key.replace(`${LOCALSTORAGE_KEY}_WEATHER_PARAMS_`, "");
      if (!id) {
        continue;
      }

      const latitude = Number(params.latitude);
      const longitude = Number(params.longitude);

      weathers.push({
        elementId: id,
        city: typeof params.city === "string" ? params.city : undefined,
        country: typeof params.country === "string" ? params.country : undefined,
        latitude: Number.isFinite(latitude) ? latitude : undefined,
        longitude: Number.isFinite(longitude) ? longitude : undefined,
        units:
          params.units === "metric" ||
          params.units === "imperial" ||
          params.units === "both"
            ? params.units
            : "metric",
        language:
          typeof params.language === "string" ? params.language : undefined,
      });
    }
  }

  const stickyNotesStore = db.createObjectStore("stickyNotes", {
    keyPath: "elementId",
  });
  await Promise.all(
    stickyNotes.map(note =>
      stickyNotesStore.add({
        ...note,
      }),
    ),
  );

  const weatherConfigStore = db.createObjectStore("weatherConfig", {
    keyPath: "elementId",
  });
  await Promise.all(
    weathers.map(weather =>
      weatherConfigStore.add({
        ...weather,
      }),
    ),
  );

  const todosStore = db.createObjectStore("todos", {
    keyPath: "id",
  });
  todosStore.createIndex("id", "id");
  await Promise.all(
    todos.map(todo =>
      todosStore.add({
        ...todo,
        deadline: todo.deadline || undefined,
      }),
    ),
  );
};
